/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { memo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import capitalize from 'lodash/capitalize'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { FormattedDateTimeRange } from 'transfix-ui/components/FormattedDateTimeRange'
import { Tag, TAG_SIZE, TAG_STYLE } from 'transfix-ui/components/Tag'
import { LoadStatusTag } from 'apps/fleet-planner/components/atoms/LoadStatusTag'
import * as Types from 'apps/fleet-planner/types'
import { selectLoadById } from 'apps/fleet-planner/utils/selectors'
import { getFirstStop, getLastStop } from 'apps/fleet-planner/utils/stops'
import { IStore } from 'apps/fleet-planner/utils/types/store'
import { Avatar } from 'transfix-ui'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { DISPLAY } from 'constants/display'
import { obscurePostDeliveryStatus } from './utils'

interface ILoadsMobileRowProps {
  loadId: string
}

/**
 * Loads page table row, used only on small screens
 */
export const LoadsMobileRow = memo<ILoadsMobileRowProps>(({ loadId }) => {
  const history = useHistory()
  const load: Types.ILoad = useSelector((state: IStore) => selectLoadById(state, loadId))

  const {
    status: loadStatus,
    currentStopSequence,
    externalId,
    stops = [],
    drivers = [],
    isSample,
    isExternal,
  } = load || {}
  const status = obscurePostDeliveryStatus(loadStatus, isExternal)

  const firstStop = getFirstStop(load)
  const lastStop = getLastStop(load)
  const orderedStops = [firstStop, lastStop]

  // Only used as a fallback if no timezone is given
  const defaultTimezone = moment.tz.guess()

  const rowClickHandler = useCallback(() => history.push(`/load/${loadId}`), [history, loadId])

  return (
    <div className='loads-mobile-row' onClick={rowClickHandler}>
      <div className='mb2 body-bold text-secondary'>Load #{externalId || DISPLAY.empty}</div>
      {orderedStops.map(stop => {
        const {
          city = '',
          stateCode = '',
          startAt = '',
          endAt = '',
          displayTimezone = defaultTimezone,
        } = stop || {}
        return (
          <div className='mb2'>
            <div className='body-bold'>
              {capitalize(city)}, {stateCode}
            </div>
            <div>
              {endAt ? (
                <FormattedDateTimeRange
                  startAt={startAt}
                  startAtTimezone={displayTimezone as AllowedTimezones}
                  endAt={endAt}
                  endAtTimezone={displayTimezone as AllowedTimezones}
                  format='time'
                />
              ) : (
                <FormattedDateTime
                  datetime={startAt}
                  format='timeWeekdayDateAndYear'
                  showTimezone
                  displayTimezone={displayTimezone as AllowedTimezones}
                />
              )}
            </div>
          </div>
        )
      })}
      <div>
        <span className='dib mr2'>
          {drivers.length ? (
            drivers
              .filter(d => !isEmpty(d))
              .map(({ name }) => {
                return <Avatar name={name} hideTooltip />
              })
          ) : (
            <Avatar name={undefined} hideTooltip />
          )}
        </span>
        <span className='dib mr2'>
          <LoadStatusTag
            status={status}
            stops={stops}
            currentStopSequence={Number(currentStopSequence)}
          />
        </span>
        {isSample && (
          <span className='dib'>
            <Tag label='Sample Load' size={TAG_SIZE.small} tagStyle={TAG_STYLE.success} />
          </span>
        )}
      </div>
    </div>
  )
})
